import type { BrokerCapabilities, BrokerCsvImportCapabilities } from "../services/brokerIntegrationApi";
import { isCtraderBroker } from "./ctraderBroker";
import { isMt4Broker } from "./mt4Broker";
import { isMt5Broker } from "./mt5Broker";
import { isTradingViewBroker } from "./tradingViewBroker";
import { isTradingViewPaperBroker } from "./tradingViewPaperBroker";

export const EXECUTING_BROKER_FIELD = "executing_broker_name";

const EXECUTING_BROKER_PLATFORMS = new Set([
  "tradingview",
  "tradingview_paper",
  "mt4",
  "mt5",
  "ctrader",
]);

export function isExecutingBrokerNameField(field: string): boolean {
  const lower = field.toLowerCase();
  return lower === EXECUTING_BROKER_FIELD || lower === "executingbrokername";
}

export function brokerRequiresExecutingBrokerCapture(
  brokerName: string | null | undefined
): boolean {
  const slug = String(brokerName || "").toLowerCase();
  return EXECUTING_BROKER_PLATFORMS.has(slug);
}

export type ExecutingBrokerContext =
  | "connect_credentials"
  | "csv_import"
  | string;

export interface BrokerExecutingBrokerConfig {
  required: boolean;
  contexts: ExecutingBrokerContext[];
  fieldLabels: Record<string, string>;
  placeholder?: string;
  hint?: string;
}

function parseContexts(raw: unknown): ExecutingBrokerContext[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => String(c).trim()).filter(Boolean);
}

function hasContext(config: BrokerExecutingBrokerConfig, context: ExecutingBrokerContext): boolean {
  return config.contexts.some((c) => c.toLowerCase() === context.toLowerCase());
}

function parseExecutingBrokerBlock(raw: unknown): BrokerExecutingBrokerConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const fieldLabels =
    o.field_labels && typeof o.field_labels === "object"
      ? (o.field_labels as Record<string, string>)
      : {};
  return {
    required: o.required === true,
    contexts: parseContexts(o.contexts),
    fieldLabels,
    placeholder:
      typeof o.placeholder === "string" && o.placeholder.trim() ? o.placeholder.trim() : undefined,
    hint: typeof o.hint === "string" && o.hint.trim() ? o.hint.trim() : undefined,
  };
}

/** Top-level `executing_broker` from GET /broker/capabilities. */
export function resolveBrokerExecutingBroker(
  cap: BrokerCapabilities | null | undefined
): BrokerExecutingBrokerConfig | null {
  if (!cap) return null;
  const fromBlock = parseExecutingBrokerBlock(cap.executing_broker);
  if (fromBlock) return fromBlock;
  if (!brokerRequiresExecutingBrokerCapture(cap.broker_name)) return null;
  return {
    required: true,
    contexts: ["connect_credentials", "csv_import"],
    fieldLabels: {},
  };
}

export function executingBrokerFieldLabel(
  config: BrokerExecutingBrokerConfig | null | undefined,
  fallback = "Executing broker"
): string {
  const fromLabels = config?.fieldLabels?.[EXECUTING_BROKER_FIELD];
  if (typeof fromLabels === "string" && fromLabels.trim()) return fromLabels.trim();
  return fallback;
}

export function executingBrokerFieldHint(
  config: BrokerExecutingBrokerConfig | null | undefined,
  brokerName?: string | null
): string {
  if (config?.hint) return config.hint;
  const platform = String(brokerName || "").toLowerCase();
  if (platform === "tradingview") {
    return "The brokerage your backtest represents (e.g. IC Markets, FTMO), not TradingView.";
  }
  if (platform === "tradingview_paper") {
    return "Usually Paper. Use a prop/broker name only if you want that label on the account picker.";
  }
  if (platform === "mt4" || platform === "mt5") {
    return "The brokerage that issued this report (e.g. Pepperstone, IC Markets), not MetaTrader.";
  }
  if (platform === "ctrader") {
    return "The CFD/FX broker for this account (e.g. FxPro, IC Markets), not cTrader.";
  }
  return "The real brokerage behind this account (e.g. IC Markets, Pepperstone).";
}

export function resolveConnectExecutingBroker(
  cap: BrokerCapabilities | null | undefined
): BrokerExecutingBrokerConfig | null {
  const spec = resolveBrokerExecutingBroker(cap);
  if (!spec) return null;
  if (hasContext(spec, "connect_credentials")) return spec;
  const inAuth = (cap?.auth?.required_fields ?? []).some(isExecutingBrokerNameField);
  if (inAuth && brokerRequiresExecutingBrokerCapture(cap?.broker_name)) return spec;
  return null;
}

export function resolveCsvExecutingBroker(
  cap: BrokerCapabilities | null | undefined,
  csvImport: BrokerCsvImportCapabilities | null | undefined
): BrokerExecutingBrokerConfig | null {
  const spec = resolveBrokerExecutingBroker(cap);
  if (spec && hasContext(spec, "csv_import")) return spec;
  const required = csvImport?.required_fields ?? [];
  if (required.some(isExecutingBrokerNameField)) {
    return (
      spec ?? {
        required: true,
        contexts: ["csv_import"],
        fieldLabels: csvImport?.field_labels ?? {},
      }
    );
  }
  if (brokerRequiresExecutingBrokerCapture(cap?.broker_name)) {
    return (
      spec ?? {
        required: true,
        contexts: ["csv_import"],
        fieldLabels: {},
      }
    );
  }
  return null;
}

/** cTrader Open API connect does not ask for executing broker (Spotware provides it on sync). */
export function shouldShowExecutingBrokerOnConnect(
  cap: BrokerCapabilities | null | undefined
): boolean {
  if (!cap) return false;
  if (isCtraderBroker(cap.broker_name) && cap.auth?.supports_auto_sync) {
    return false;
  }
  return !!resolveConnectExecutingBroker(cap)?.required;
}

export function shouldShowExecutingBrokerOnCsvImport(
  cap: BrokerCapabilities | null | undefined,
  csvImport?: BrokerCsvImportCapabilities | null
): boolean {
  const spec = resolveCsvExecutingBroker(cap, csvImport ?? cap?.csv_import ?? cap?.xlsx_import ?? cap?.html_import);
  return !!spec?.required || brokerRequiresExecutingBrokerCapture(cap?.broker_name);
}

export function parseSavedExecutingBrokerName(
  credentials: Record<string, unknown> | null | undefined
): string {
  if (!credentials) return "";
  const raw =
    credentials.executing_broker_name ??
    credentials.executingBrokerName ??
    credentials.executing_broker;
  if (typeof raw === "string") return raw.trim();
  return "";
}

export function normalizeExecutingBrokerNameForApi(name: string): string {
  return String(name || "").trim();
}

export function mergeExecutingBrokerIntoPayload(
  payload: Record<string, unknown>,
  executingBrokerName: string,
  config: BrokerExecutingBrokerConfig | null | undefined
): Record<string, unknown> {
  const { executing_broker_name: _a, executingBrokerName: _b, ...rest } = payload;
  if (!config?.required && !brokerRequiresExecutingBrokerCapture(payload.broker_name as string)) {
    return rest;
  }
  const normalized = normalizeExecutingBrokerNameForApi(executingBrokerName);
  if (!normalized) return rest;
  return { ...rest, executing_broker_name: normalized };
}

export function isStatementImportPlatform(brokerName: string | null | undefined): boolean {
  return (
    isTradingViewBroker(brokerName) ||
    isTradingViewPaperBroker(brokerName) ||
    isMt5Broker(brokerName) ||
    isMt4Broker(brokerName) ||
    isCtraderBroker(brokerName)
  );
}
